(() => {
  'use strict';

  // Singleton guard — prevents double-bind on theme editor reloads or refreshDrawer re-renders
  if (document._mpAbandonBound) return;
  document._mpAbandonBound = true;

  const STORAGE_KEY = 'mp_checkout_abandon';
  const TTL_MS = 30 * 60 * 1000; // 30 minutes

  // --- Storage helpers (wrapped per theme convention) ---

  function setFlag() {
    try {
      sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (_) {}
  }

  function getFlag() {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch (_) {
      return null;
    }
  }

  function clearFlag() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
  }

  // --- SET: capture-phase delegated listener on document ---
  // Every way a shopper can reach checkout has to set the flag, not just the
  // drawer. The cart page submits <button name="checkout"> inside the cart
  // form (sections/static-cart.liquid, twice), and the campaign spending bar
  // links straight out — neither matched the original drawer-only selector,
  // so leaving from those pages showed no popup on return.

  const CHECKOUT_SELECTOR = [
    '.cart-drawer__checkout-btn',
    '[name="checkout"]',
    '.campaign-bar__checkout',
    'a[href^="/checkout"]'
  ].join(',');

  document.addEventListener('click', function onCheckoutClick(e) {
    if (e.target.closest(CHECKOUT_SELECTOR)) {
      setFlag();
    }
  }, true); // capture phase

  // --- Popup references (resolved once on first show) ---

  let popup, overlay, closeBtn, dismissBtn, returnBtn;

  function resolveElements() {
    popup      = document.querySelector('[data-mp-abandon-popup]');
    overlay    = popup && popup.querySelector('[data-mp-abandon-overlay]');
    closeBtn   = popup && popup.querySelector('[data-mp-abandon-close]');
    dismissBtn = popup && popup.querySelector('[data-mp-abandon-dismiss]');
    returnBtn  = popup && popup.querySelector('[data-mp-abandon-return]');
  }

  // --- Named close-path handlers (required for removeEventListener) ---

  function onEsc(e) {
    if (e.key === 'Escape') closePopup();
  }

  function onOverlayClick(e) {
    if (e.target === overlay) closePopup();
  }

  // --- Show / Close ---

  function showPopup() {
    if (!popup) resolveElements();
    if (!popup) return;

    // Already open — a second pageshow (bfcache restore) would otherwise bind
    // the close handlers twice and leave one set attached after closing.
    if (popup.classList.contains('mp-abandon-popup--open')) return;

    popup.classList.add('mp-abandon-popup--open');
    popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mp-popup-open');

    document.addEventListener('keydown', onEsc);
    overlay.addEventListener('click', onOverlayClick);
    closeBtn.addEventListener('click', closePopup);
    dismissBtn.addEventListener('click', closePopup);

    // Focus trap — first interactive element
    const firstFocusable = returnBtn || closeBtn;
    if (firstFocusable) firstFocusable.focus();
  }

  function closePopup() {
    if (!popup) return;

    clearFlag();

    popup.classList.remove('mp-abandon-popup--open');
    popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('mp-popup-open');

    document.removeEventListener('keydown', onEsc);
    overlay.removeEventListener('click', onOverlayClick);
    closeBtn.removeEventListener('click', closePopup);
    dismissBtn.removeEventListener('click', closePopup);
  }

  // --- READ: pageshow fires on fresh load AND bfcache restores ---
  // Site-wide — return page is unknown (drawer can be on any page)

  window.addEventListener('pageshow', function onPageShow() {
    const raw = getFlag();
    if (!raw) return;

    const elapsed = Date.now() - Number(raw);

    if (elapsed >= TTL_MS) {
      // Stale — discard silently
      clearFlag();
      return;
    }

    // An empty cart means the shopper did not abandon anything — either they
    // completed the order, which empties the cart, or they cleared it at
    // checkout. Without this the popup told a paying customer they had left
    // something behind, and "Return to Checkout" led to an empty basket.
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        if (cart && cart.item_count === 0) {
          clearFlag();
          return;
        }
        // Valid abandon — show popup
        // Small defer so the page is fully painted before the popup appears
        requestAnimationFrame(showPopup);
      })
      .catch(function () {
        // Cart unreachable — fall back to the previous behaviour rather than
        // swallowing the popup entirely.
        requestAnimationFrame(showPopup);
      });
  });

})();
